import { apiConfig } from '../../config/config.bundle';
import type {
  Address,
  ApiResponse,
  ApproveRegistrationResponse,
  CustomerAccountResponse,
  LocalUserAccount,
  MelissaAddressValidationResult,
  PartyAddress,
  RegistrationResult,
  UserAccount,
} from '../../types/api-types';
import { enhanceApi } from '../../utils/api-utils';
import type { RegistrationFormData } from '../../utils/schemas/auth/registration-schema';
import { getScopedApi } from './base-api';

const AccountServiceConfig = apiConfig.account;

type UserIdValidationQueryArg = {
  userId: string;
};

type AddressValidationQueryArg = Partial<Address> & {
  zip: string;
  country: string;
};

type TaxIdValidationQueryArg = Partial<RegistrationFormData> & {
  taxId: RegistrationFormData['taxId'];
};

type RegisterMutationArg = RegistrationFormData & {
  shippingAddress: Omit<
    RegistrationFormData['shippingAddress'],
    'postalCode'
  > & { zipCode: string };
  billingAddress: Omit<RegistrationFormData['billingAddress'], 'postalCode'> & {
    zipCode: string;
  };
};

type CreatePropertyMutationArg = Omit<
  RegistrationFormData,
  'password' | 'confirmPassword' | 'isTermsAccepted' | 'recaptchaToken'
> & {
  shippingAddress: Omit<
    RegistrationFormData['shippingAddress'],
    'postalCode'
  > & { zipCode: string };
  billingAddress: Omit<RegistrationFormData['billingAddress'], 'postalCode'> & {
    zipCode: string;
  };
  deliveryInstructions?: string;
  userIdTaxId?: string;
  accountNumber?: string;
  skipTaxValidation?: boolean;
};

type RiskNotificationEmailTemplate = {
  templateName: 'RISK_REVIEW_TEMPLATE';
  firstName: string;
  lastName: string;
  emailTo: string;
  companyName: string;
  emailFrom: string;
};

type ShareSpecificationsEmailTemplate = {
  templateName: 'SHARE_SPEC_TEMPLATE';
  emailTo: string;
  senderName: string;
  items: { sku: string; specLink: string }[];
  description: string;
  additionalMessage?: string;
};

type InviteUserEmailTemplate = {
  emailTo: string;
  templateName: 'INVITE_USER_TEMPLATE';
  companyName: string;
  inviterName: string;
  inviterEmail: string;
  acceptLink: string;
  expiresAt: number;
};

type CreateOdrEmailNotificationItem = {
  item: string;
  requestedDeliveryDate: string;
  productDescription: string;
  productImageUrl: string;
};

type CreateOdrEmailNotificationTemplate = {
  emailSubject: string;
  emailTo: string;
  templateName: 'CREATE_ORDER_EMAIL_NOTIFICATION';
  orderNumber: string | number;
  referenceNumber: string;
  firstName: string;
  poNumber: string;
  orderDate: string;
  propertyName: string;
  deliveryAddress: string;
  requestedDeliveryDate: string;
  deliveryMethod: string;
  items: CreateOdrEmailNotificationItem[];
  manageOrderUrl: string;
  xtYear: number;
};

type SendEmailMutationArg =
  | RiskNotificationEmailTemplate
  | ShareSpecificationsEmailTemplate
  | InviteUserEmailTemplate
  | InvoiceEmailReceiptRequest
  | CreateOdrEmailNotificationTemplate
  | {
      emailTo: string;
      userId?: string;
      templateName: string;
      requestNumber?: string;
      firstName?: string;
      lastName?: string;
      companyName?: string;
      emailFrom?: string;
      inviterName?: string;
      inviterEmail?: string;
      acceptLink?: string;
      properties?: PartyAddress[];
      logoUrl?: string;
      accountNumber?: string;
    };

type AddressBYPartyIdQueryArg = {
  partyId: number;
};

export type RegisterInviteUserMutationArg = {
  userId: string;
  firstName: string;
  lastName: string;
  emailId: string;
  companyName?: string;
  accountNumber?: string | number;
  password: string;
  inviterUserId: string;
  selectedProperties:
    | PartyAddress[]
    | {
        ADDRESS1: string;
        CITY: string;
        STATE: string;
        COUNTY: string;
        COUNTRY: string;
        POSTAL_CODE: string;
      }[];
  phoneNumber?: string;
  userRole?: string;
  isInviteUserForNewAccount: boolean;
  existingUserAccount?: ApproveRegistrationResponse;
  addProperty?: boolean;
  invitedUserAccounts?: string[] | number[];
};

type CustomerAccountResponseQueryArg = {
  accountNumber: string | number;
};

type DeleteUserFromAccountArg = {
  userId: string | number;
  accountNumber: string | number;
  userName: string;
  userEmailId: string;
  deleteType: 'all' | 'selected';
  adminEmailId: string;
  personPartyId: string | number;
};

export type InvoiceEmailReceiptRequest = {
  templateName: string;
  firstName: string;
  orderNumber: string;
  paymentReceivedDate: string;
  customerReference: string;
  confirmationNo: string;
  emailTo: string;
  xtYear: string;
};

const accountApi = getScopedApi('account').injectEndpoints({
  endpoints: builder => ({
    token: builder.query<ApiResponse<string>, void>({
      query: () => ({
        method: 'GET',
        url: AccountServiceConfig.paths.accounts,
        params: { requestType: 'getToken' },
        headers: {
          Authorization: 'unauthorized',
        },
      }),
    }),
    botToken: builder.query<
      ApiResponse<string>,
      { userId: string; accountNumber: string; locationId?: number }
    >({
      query: arg => ({
        method: 'GET',
        url: AccountServiceConfig.paths.accounts,
        params: { requestType: 'getBotToken', ...arg },
      }),
    }),
    userIdValidation: builder.query<
      ApiResponse<boolean>,
      UserIdValidationQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: AccountServiceConfig.paths.accounts,
        params: { requestType: 'validateUser', ...arg },
      }),
    }),
    addressValidation: builder.query<
      ApiResponse<MelissaAddressValidationResult>,
      AddressValidationQueryArg
    >({
      query: arg => ({
        method: 'GET',
        url: AccountServiceConfig.paths.accounts,
        params: { requestType: 'validateAddress', ...arg },
      }),
    }),
    taxIdValidation: builder.query<
      ApiResponse<string>,
      TaxIdValidationQueryArg
    >({
      query: arg => {
        return {
          method: 'POST',
          url: AccountServiceConfig.paths.accounts,
          body: arg,
          params: { requestType: 'taxValidation' },
        };
      },
    }),
    register: builder.mutation<
      ApiResponse<RegistrationResult>,
      RegisterMutationArg
    >({
      query: arg => {
        return {
          method: 'POST',
          url: AccountServiceConfig.paths.accounts,
          body: arg,
          params: { requestType: 'createAccount' },
        };
      },
    }),
    approveRegistrationRequest: builder.mutation<
      ApiResponse<ApproveRegistrationResponse>,
      UserAccount
    >({
      query: arg => ({
        method: 'POST',
        url: AccountServiceConfig.paths.accounts,
        body: arg,
        params: { requestType: 'approveUserAccount' },
      }),
    }),
    acceptConsent: builder.mutation<ApiResponse, UserAccount>({
      query: arg => ({
        method: 'POST',
        url: AccountServiceConfig.paths.accounts,
        body: arg,
        params: { requestType: 'consent' },
      }),
    }),
    createProperty: builder.mutation<
      ApiResponse<RegistrationResult>,
      CreatePropertyMutationArg
    >({
      query: arg => {
        return {
          method: 'POST',
          url: AccountServiceConfig.paths.accounts,
          body: arg,
          params: { requestType: 'addUserAccount' },
        };
      },
    }),
    updateAccountDetails: builder.mutation<ApiResponse, UserAccount>({
      query: arg => ({
        method: 'POST',
        url: AccountServiceConfig.paths.accounts,
        body: arg,
        params: { requestType: 'updateAccount' },
      }),
    }),
    updateRequestStatus: builder.mutation<ApiResponse, UserAccount>({
      query: arg => ({
        method: 'PUT',
        url: AccountServiceConfig.paths.accounts,
        body: arg,
        params: { requestType: 'updateAccountStatus' },
      }),
    }),
    sendEmail: builder.mutation<ApiResponse<void>, SendEmailMutationArg>({
      query: arg => {
        return {
          method: 'POST',
          url: AccountServiceConfig.paths.accounts,
          body: arg,
          params: { requestType: 'sendEmail' },
        };
      },
    }),
    addressesByPartyId: builder.query<
      ApiResponse<PartyAddress[]>,
      AddressBYPartyIdQueryArg
    >({
      query: arg => {
        return {
          method: 'GET',
          url: AccountServiceConfig.paths.accounts,
          params: { requestType: 'getDataByPartyId', ...arg },
        };
      },
      providesTags: ['AddressesByPartyId'],
    }),
    registerInviteUser: builder.mutation<
      ApiResponse<void>,
      RegisterInviteUserMutationArg
    >({
      query: arg => {
        return {
          method: 'POST',
          url: AccountServiceConfig.paths.accounts,
          body: arg,
          params: { requestType: 'inviteUserAccount' },
        };
      },
    }),
    addUserForNewAccount: builder.mutation<
      ApiResponse<void>,
      RegisterInviteUserMutationArg
    >({
      query: arg => ({
        method: 'POST',
        url: AccountServiceConfig.paths.accounts,
        body: arg,
        params: { requestType: 'ADD_USER_FOR_NEW_USER_ACCOUNT' },
      }),
    }),
    customerAccountResponse: builder.query<
      ApiResponse<CustomerAccountResponse>,
      CustomerAccountResponseQueryArg
    >({
      query: arg => {
        return {
          method: 'GET',
          url: AccountServiceConfig.paths.accounts,
          params: { requestType: 'getCustomerAccountResponse', ...arg },
        };
      },
    }),
    updateUserAccounts: builder.mutation<ApiResponse, LocalUserAccount>({
      query: arg => ({
        method: 'PUT',
        url: AccountServiceConfig.paths.accounts,
        body: arg,
        params: { requestType: 'updateUserAccounts' },
      }),
      invalidatesTags: ['LocalUsersOfAccount'],
    }),
    deleteUserFromAllAccount: builder.mutation<
      ApiResponse,
      DeleteUserFromAccountArg
    >({
      query: arg => ({
        method: 'DELETE',
        url: AccountServiceConfig.paths.accounts,
        body: arg,
        params: { requestType: 'deleteUserFromAllAccount' },
      }),
      invalidatesTags: ['LocalUsersOfAccount'],
    }),
    deleteUserFromSelectedAccount: builder.mutation<
      ApiResponse,
      DeleteUserFromAccountArg
    >({
      query: arg => ({
        method: 'DELETE',
        url: AccountServiceConfig.paths.accounts,
        body: arg,
        params: { requestType: 'deleteUserFromSelectedAccount' },
      }),
      invalidatesTags: [
        'LocalUsersOfAccount',
        'PropertyGroups',
        'AddressesByPartyId',
      ],
    }),
  }),
});

export const {
  useTokenQuery,
  useLazyTokenQuery,
  useBotTokenQuery,
  useLazyBotTokenQuery,
  useUserIdValidationQuery,
  useLazyUserIdValidationQuery,
  useAddressValidationQuery,
  useLazyAddressValidationQuery,
  useTaxIdValidationQuery,
  useLazyTaxIdValidationQuery,
  useRegisterMutation,
  useApproveRegistrationRequestMutation,
  useAcceptConsentMutation,
  useCreatePropertyMutation,
  useUpdateAccountDetailsMutation,
  useUpdateRequestStatusMutation,
  useSendEmailMutation,
  useAddressesByPartyIdQuery,
  useLazyAddressesByPartyIdQuery,
  useRegisterInviteUserMutation,
  useAddUserForNewAccountMutation,
  useCustomerAccountResponseQuery,
  useLazyCustomerAccountResponseQuery,
  useUpdateUserAccountsMutation,
  useDeleteUserFromAllAccountMutation,
  useDeleteUserFromSelectedAccountMutation,
} = enhanceApi(accountApi);

export default accountApi;
